import { doc, setDoc, getDoc, addDoc, collection, getDocs, query, where, updateDoc } from "firebase/firestore";
import { NotificationType } from "../../constants/GlobalTypesAndEnumsAndConstants";
import { db } from "../utils/init-firebase";
import { arrayElementLooper, trackEvent } from "../utils/helper-methods";
import { syncUserToSmashSend } from "../utils/emailAndSmashsendHelperMethods";
import {
  ARTICLES_SAVED_COLLECTION_NAME,
  ArticleToSaveObject,
  USERS_COLLECTION_NAME,
  UserData,
  DECK_COLLECTION_NAME,
  DeckObject,
  LEADERBOARD_COLLECTION_NAME,
  JUNGLE_BACKEND_DOMAIN_FOR_ROUTES_MIGRATED_TO_CLOUDFLARE_WORKER,
  STREAKS_COLLECTION_NAME,
} from "../../constants/GlobalTypesAndEnumsAndConstants";
import { cardStore } from "../../zustand/globalStore";

export const getUser = async (userId: string) => {
  const userDoc = doc(db, USERS_COLLECTION_NAME, userId);
  let returnData: any = null;

  await getDoc(userDoc)
    .then((snapshot: any) => {
      if (snapshot.exists()) {
        returnData = snapshot.data();
        returnData.id = snapshot.id;
      } else {
        // console.log("User with that id does not exist");
      }
    })
    .catch((err) => {
      // console.log(err);
    });
  return returnData;
};

export const createUser = async (userId: string, userData: UserData) => {
  // Adding a new user with a Firebase with userId
  const userDoc = doc(db, USERS_COLLECTION_NAME, userId);
  let userSnapshot = await getDoc(userDoc);
  if (!userSnapshot.exists()) {
    await setDoc(doc(db, USERS_COLLECTION_NAME, userId), userData);
    const user: any = userData;
    user.id = userId;
    return user;
  }
  let user = userSnapshot.data();
  user.id = userId;
  return user;
};

export const getUserById = async (userId: string) => {
  const userDoc = doc(db, USERS_COLLECTION_NAME, userId);
  let userSnapshot = await getDoc(userDoc);
  let user = userSnapshot.data();
  if (!user) {
    return null;
  }
  user.id = userId;
  return user;
};

export const getUserByEmail = async (email_to_search: string) => {
  //apply where clause to get user by email
  const usersCollectionRef = query(collection(db, USERS_COLLECTION_NAME), where("email_auth", "==", email_to_search));
  const userDocs = await getDocs(usersCollectionRef);
  return arrayElementLooper(userDocs.docs);
};

export const getUserBySubscriptionId = async (subscription_id: string) => {
  //apply where clause to get user by subscription id
  const usersCollectionRef = query(
    collection(db, USERS_COLLECTION_NAME),
    where("subscription_information.subscription_id", "==", subscription_id)
  );
  const userDocs = await getDocs(usersCollectionRef);
  return arrayElementLooper(userDocs.docs);
};

export const updateUser = async (userId: string, objectWithChange: any) => {
  // Account language is revision-checked by its owner API. Older settings forms
  // may replay a profile snapshot; those saves must never restore stale locale authority.
  if (objectWithChange?.user_settings && typeof objectWithChange.user_settings === "object") {
    const settings = Object.fromEntries(Object.entries(objectWithChange.user_settings).filter(([field]) =>
      !["app_language", "app_language_preference", "app_language_preference_revision", "app_language_updated_at", "app_language_source"].includes(field)));
    objectWithChange = { ...objectWithChange, user_settings: settings };
    if (Object.keys(settings).length === 0) {
      const { user_settings, ...otherChanges } = objectWithChange;
      objectWithChange = otherChanges;
      if (Object.keys(otherChanges).length === 0) return;
    }
  }
  const userDoc = doc(db, `${USERS_COLLECTION_NAME}`, userId);

  await setDoc(userDoc, objectWithChange, { merge: true })
    .then(() => {
      // console.log("User has been updated");
    })
    .catch((err) => {
      // console.log("Error: User failed to update: ", err);
    });

  // If any of these fields changed, we sync the user data with SMASHSEND
  // this guarantees SMASHSEND is always in sync with our DB!! 🚀🚀🚀
  const FIELDS_TO_SYNC_WITH_CRM = [
    "name",
    "user_settings",
    "number_of_questions_answered",
    "number_of_sets_of_cards_generated",
    "number_of_chat_messages_sent",
    "date_of_last_visit",
    "date_of_last_chat_message_sent",
    "subscription_information",
    "timezone",
    "country_code",
    "acquisitionSource",
    "preferences",
    "key_features_user_has_tried",
  ];

  const FIELDS_WITH_USAGE_MILESTONE_VALIDATIONS = [
    "number_of_questions_answered",
    "number_of_sets_of_cards_generated",
    "number_of_chat_messages_sent",
  ];

  const hasReachedMilestoneThreshold = (value: unknown, multiple: number, minimumExclusiveThreshold: number) => {
    if (typeof value !== "number" || !Number.isFinite(value)) {
      return false;
    }

    if (value <= minimumExclusiveThreshold) {
      return false;
    }

    return value % multiple === 0;
  };

  const changedCrmFields = Object.keys(objectWithChange).filter((key) => FIELDS_TO_SYNC_WITH_CRM.includes(key));
  const needsCrmSync = changedCrmFields.length > 0;
  if (needsCrmSync) {
    const hasFieldThatRequiresImmediateSync = changedCrmFields.some(
      (key) => !FIELDS_WITH_USAGE_MILESTONE_VALIDATIONS.includes(key)
    );
    const usageMilestoneReached =
      hasReachedMilestoneThreshold(objectWithChange?.number_of_questions_answered, 25, 5) ||
      hasReachedMilestoneThreshold(objectWithChange?.number_of_sets_of_cards_generated, 10, 2) ||
      hasReachedMilestoneThreshold(objectWithChange?.number_of_chat_messages_sent, 10, 10);
    // We only sync to SmashSend when either:
    // 1. A non-rate-limited field has been modified.
    // 2. One of the gated usage milestones has been hit (reduces unnecessary sync noise).
    if (hasFieldThatRequiresImmediateSync || usageMilestoneReached) {
      await syncUserToSmashSend(userId).catch((err) => {
        trackEvent(
          "Failed to Sync User to SmashSend",
          {
            userId,
            error: err instanceof Error ? err.message : "Unknown error",
            fieldsAttemptedToSync: changedCrmFields,
          },
          { id: userId } as UserData
        );
      });
    }
  }

  // Update user name for leaderboard
  if (objectWithChange.name) {
    const leaderboardUserRef = doc(db, `${LEADERBOARD_COLLECTION_NAME}`, userId);
    const leaderboardUseDoc = await getDoc(leaderboardUserRef);

    if (leaderboardUseDoc.exists()) {
      const leaderboardUpdate: any = {};
      if (objectWithChange.name) leaderboardUpdate.user_name = objectWithChange.name;

      await setDoc(leaderboardUserRef, leaderboardUpdate, { merge: true }).catch((err) => {});
    }
  }
};

export const createPageGenerationDataDocumentForUser = async (userId: string, generatedDeck: DeckObject) => {
  const pageGenerationDataWithoutUndefinedFields = Object.fromEntries(
    Object.entries(generatedDeck).map(([key, value]) => [key, value !== undefined ? value : null])
  );
  addDoc(
    collection(db, `${USERS_COLLECTION_NAME}/${userId}/${DECK_COLLECTION_NAME}`),
    pageGenerationDataWithoutUndefinedFields
  )
    .then((snapshot) => {
      return snapshot.id;
    })
    .catch((err) => {
      // console.log(err);
      return null;
    });
};

// we save an article to the DB when the user clicks the "generate flashcards" button
export const saveArticleToDB = async (userId: string, articleObjectToSave: ArticleToSaveObject) => {
  addDoc(collection(db, `${USERS_COLLECTION_NAME}/${userId}/${ARTICLES_SAVED_COLLECTION_NAME}`), articleObjectToSave)
    .then((snapshot) => {
      // console.log("Saved article to DB", snapshot.id);
      return snapshot.id;
    })
    .catch((err) => {
      // console.log(err);
      return null;
    });
};

// Function to save data needed to send notification to those who run out of chat messages or answer feedbacks.
// Send the notification once the feedback/messages have been replenished (after an hour), but activiate this
// function as soon as they run out to store appropriate data in firebase using backend methods.
const saveNotificationDataToBeSentLaterForAnswerFeedbacksOrChatMessagesReplenished = async (
  userId,
  notification_type: NotificationType
) => {
  try {
    // Call the backend API route to trigger function to save data
    const response = await fetch(
      `${JUNGLE_BACKEND_DOMAIN_FOR_ROUTES_MIGRATED_TO_CLOUDFLARE_WORKER}/notifications/save_notification_data_to_be_sent_later_for_answer_feedbacks_or_chat_messages_replenished`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userId, notification_type }),
      }
    );

    if (!response.ok) {
      console.error("Failed to trigger save user data method:", await response.text());
    }
  } catch (error) {
    console.error("Error calling backend endpoint:", error);
  }
};

export const subtractOneFromChatMessagesRemainingForUser = async (
  user: UserData,
  userNumOfChatMessagesRemaining: number
) => {
  if (!user) return;

  // if user is in mini review mode, we will let them use chat and feedbacks feature for free
  const isUserInMiniReviewMode = cardStore.getState().isUserInMiniReviewMode;
  if (isUserInMiniReviewMode) {
    return;
  }

  const updateObject: any = {
    date_of_last_chat_message_sent: new Date(),
    number_of_chat_messages_sent: (user?.number_of_chat_messages_sent || 0) + 1,
    subscription_information: {
      num_of_chat_messages_remaining: userNumOfChatMessagesRemaining - 1,
    },
  };

  // add the last_date_answer_feedbacks_ran_out field if this is the last feedback
  if (userNumOfChatMessagesRemaining === 1) {
    updateObject.subscription_information.last_date_chat_messages_ran_out = new Date();
  }

  // Calling function to save user data for answer feedbacks/chat messages notification if they have none remaining
  if (userNumOfChatMessagesRemaining - 1 === 0) {
    saveNotificationDataToBeSentLaterForAnswerFeedbacksOrChatMessagesReplenished(
      user.id,
      NotificationType.ChatMessagesReplenished
    );
  }

  await updateUser(user?.id, updateObject).catch((err) => {
    // dot not show error to user
  });
};

// ******** SUBSCRIPTIONS METHODS ******** //
// ******** SUBSCRIPTIONS METHODS ******** //

export const updateAllUsers = async () => {
  const usersCollectionRef = collection(db, `${USERS_COLLECTION_NAME}`);
  const usersCollection = await getDocs(usersCollectionRef);
  const users = arrayElementLooper(usersCollection.docs);
  let index = 0;
  for (let user of users) {
    //await updateUser(user.id,{version:null})
    console.log(index);
    console.log(user.id);
    index++;
  }
  console.log("...finish");
};

export const checkAndUpdateUserTimezone = async (user) => {
  if (!user) return;
  // Check if the user object does not have a defined timezone or if the timezone is different from the user's current timezone.
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (!user?.timezone || user?.timezone !== timezone) {
    // Update the user's timezone in our database and SMASHSEND
    await updateUser(user.id, {
      timezone: timezone,
    }).catch((err) => {
      console.log("Error: Failed to update user timezone: ", err);
    });

    // Update the timezone in streaks collection
    const streakDocRef = doc(db, STREAKS_COLLECTION_NAME, user.id);
    const streakDoc = await getDoc(streakDocRef);
    if (streakDoc.exists()) {
      updateDoc(streakDocRef, { user_timezone: timezone });
    }
  }
};

// For manual bug fixing

// createUser("mKdfJnmsdnc7iu4lKPfJIRLHnS53", {
//   email_auth: "cedric.sillaber@gmail.com",
//   email_communication: "cedric.sillaber@gmail.com",
//   authentication_provider: "Email",
//   name: null,
//   date_created: new Date(),
//   subscription_information: {
//     num_of_monthly_generations_left:
//       NUM_OF_FREE_MONTHLY_GENERATIONS_ALLOWED,
//     subscription_status: SubscriptionStatus.ACTIVE,
//     first_time_subscription_activated_date: new Date(),
//   },
//   user_type: null,
//   student_type: null,
// });
